// ============================================================================
// useExpertiseOptions.js — Supabase CRUD for the expertise_options master list
// ============================================================================
// Stores selectable expertise tags in a dedicated Supabase table.
// Provides fetch + add-new-option functionality.
// ============================================================================

import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient';

// Fallback initial options — only used if the Supabase table is empty
// (seeded via migration; this is a safety net only)
const SEED_OPTIONS = [
  'Communicative', 'Client communication', 'Presentation skills',
  'Leadership', 'Team coordination', 'Organising', 'Planning',
  'Time management', 'Problem solving', 'Concept thinking',
  'Spatial insight', 'Graphic design', 'Visualization', 'Rendering',
  'Hand sketches', 'Technical drawing', 'Detailing',
  'Construction detailing', 'Interior detailing', 'Material knowledge',
  'Material knowledge (Timber)', 'Material knowledge (Stone)',
  'Material knowledge (Steel)', 'Material knowledge (Concrete)',
  'Material knowledge (Glass)', 'BIM / Revit', '3D modelling',
  'Parametric thinking', 'Research', 'Sustainability knowledge',
  'Building regulations', 'Site coordination', 'Consultant coordination',
  'Documentation', 'Precision', 'Mentoring', 'Model making',
  'Diagramming', 'Specification writing',
];

export function useExpertiseOptions() {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchOptions = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('expertise_options')
        .select('id, label')
        .order('label', { ascending: true });

      if (error) {
        // Table may not exist yet — fall back to seed list
        console.warn('[useExpertiseOptions] fetch error:', error.message);
        setOptions(SEED_OPTIONS.map((label, i) => ({ id: i, label })));
      } else if (data && data.length > 0) {
        setOptions(data);
      } else {
        // Table exists but is empty — seed it
        await seedOptions();
      }
    } catch (err) {
      console.warn('[useExpertiseOptions] fetch error:', err.message);
      setOptions(SEED_OPTIONS.map((label, i) => ({ id: i, label })));
    } finally {
      setLoading(false);
    }
  }, []);

  const seedOptions = async () => {
    try {
      const rows = SEED_OPTIONS.map(label => ({ label }));
      const { data, error } = await supabase
        .from('expertise_options')
        .insert(rows)
        .select('id, label');
      if (error) throw error;
      setOptions((data || []).sort((a, b) => a.label.localeCompare(b.label)));
    } catch (err) {
      console.warn('[useExpertiseOptions] seed error:', err.message);
      setOptions(SEED_OPTIONS.map((label, i) => ({ id: i, label })));
    }
  };

  useEffect(() => { fetchOptions(); }, [fetchOptions]);

  /**
   * Add a new expertise option to the master list.
   * Returns { success, option } or { success: false, error }.
   * Prevents duplicates (case-insensitive).
   */
  const addOption = async (label) => {
    const trimmed = (label || '').trim();
    if (!trimmed) return { success: false, error: 'Empty value' };

    // Duplicate check (case-insensitive)
    const exists = options.some(o => o.label.toLowerCase() === trimmed.toLowerCase());
    if (exists) return { success: false, error: 'Option already exists' };

    try {
      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.user) throw new Error('Please sign in');

      const { data, error } = await supabase
        .from('expertise_options')
        .insert([{ label: trimmed }])
        .select('id, label')
        .single();
      if (error) throw error;

      setOptions(prev => [...prev, data].sort((a, b) => a.label.localeCompare(b.label)));
      return { success: true, option: data };
    } catch (err) {
      return { success: false, error: err.message };
    }
  };

  return { options, loading, addOption, refetch: fetchOptions };
}
